import OnboardingLayout from './OnboardingLayout';
import { trainingVideos, resourceSections } from '../Resources/data';

const allResourceItems = resourceSections.flatMap((section) => section.items);
const gettingStartedVideo = trainingVideos.find((video) => video.id === 'getting-started-dashboard');
const teamTraining = allResourceItems.find((item) => item.id === 'team-training');
const inOfficeScripts = allResourceItems.find((item) => item.id === 'in-office-scripts');

/*
 * Figma's NEXT_STEPS cards click through to nowhere real (all three just navigate home in the
 * prototype). These map to real content already in src/Dashboard/Resources/data.js, so each
 * card links/opens for real: the video card navigates to the Resources tab, the two document
 * cards open their actual Google Docs/Drive href in a new tab.
 */
const NEXT_STEPS = [
    {
        gradient: 'linear-gradient(148.51deg, rgb(71,81,73) 7.31%, rgb(13,26,18) 92.69%)',
        category: `Training video · ${gettingStartedVideo?.duration ?? '5 min'}`,
        title: gettingStartedVideo?.title ?? 'Getting Started',
        description: gettingStartedVideo?.description ?? '',
        cta: 'Watch now →',
        to: '/dashboard/resources',
    },
    {
        gradient: 'linear-gradient(148.95deg, rgb(45,57,50) 7.31%, rgb(17,17,8) 92.69%)',
        category: 'Documentation',
        title: teamTraining?.title ?? 'Train Your Team',
        description: teamTraining?.description ?? '',
        cta: 'Open guide →',
        href: teamTraining?.href,
    },
    {
        gradient: 'linear-gradient(148.51deg, rgb(79,81,70) 7.31%, rgb(21,22,16) 92.69%)',
        category: 'Scripts & templates',
        title: inOfficeScripts?.title ?? 'In-Office Scripts',
        description: inOfficeScripts?.description ?? '',
        cta: 'View scripts →',
        href: inOfficeScripts?.href,
    },
];

/* Same color-mix() approach as the other v2 pages — Tailwind's opacity modifier (including
 * group-hover: variants of it) doesn't work on our var(--cv2-*)-based custom colors. The
 * `.group`/`.group:hover` selectors below piggyback on Tailwind's own `group` marker class. */
const SCOPED_STYLES = `
  .cv2-done-eyebrow { color: color-mix(in srgb, var(--cv2-foreground) 28%, transparent); }
  .cv2-done-subcopy { color: color-mix(in srgb, var(--cv2-foreground) 38%, transparent); }
  .cv2-done-card-row { border-color: color-mix(in srgb, var(--cv2-foreground) 6%, transparent); }
  .cv2-done-category { color: color-mix(in srgb, var(--cv2-foreground) 35%, transparent); }
  .cv2-done-desc { color: color-mix(in srgb, var(--cv2-foreground) 38%, transparent); }
  .cv2-done-cta { color: color-mix(in srgb, var(--cv2-foreground) 50%, transparent); transition: color 0.2s ease; }
  .group:hover .cv2-done-cta { color: var(--cv2-foreground); }
  .cv2-done-pill {
    background-color: color-mix(in srgb, var(--cv2-foreground) 4%, transparent);
    border-color: color-mix(in srgb, var(--cv2-foreground) 12%, transparent);
    transition: border-color 0.2s ease;
  }
  .group:hover .cv2-done-pill { border-color: color-mix(in srgb, var(--cv2-foreground) 28%, transparent); }
  .cv2-done-viewall { color: color-mix(in srgb, var(--cv2-foreground) 25%, transparent); transition: color 0.15s ease; }
  .cv2-done-viewall:hover { color: color-mix(in srgb, var(--cv2-foreground) 45%, transparent); }
`;

/*
 * The gradient is the whole card, not a swatch beside it — these ramps are built to sit under
 * their own text and read as sludge when shrunk to a small block next to transparent content.
 */
const NextStepCard = ({ item, onLeave }) => {
    const Component = item.to ? 'button' : 'a';
    const linkProps = item.to
        ? { type: 'button', onClick: () => onLeave(item.to) }
        : { href: item.href, target: '_blank', rel: 'noopener noreferrer' };

    return (
        <Component
            {...linkProps}
            className="group flex-1 w-full text-left cv2-done-card-row border-b sm:border-b-0 sm:border-r last:border-b-0 sm:last:border-r-0"
        >
            <div
                className="h-full flex flex-col justify-end p-5 sm:p-6 min-h-[200px] sm:min-h-[260px] group-hover:brightness-110 transition-all duration-300"
                style={{ background: item.gradient }}
            >
                <div className="flex flex-col gap-1.5 mb-5 sm:mb-6">
                    <p className="font-aeonik text-lg leading-[27px] tracking-[-0.3px] text-cv2-foreground normal-case">
                        {item.title}
                    </p>
                    <p className="cv2-done-desc font-aeonik text-[13px] leading-5 normal-case">
                        {item.description}
                    </p>
                </div>

                <div className="mb-5 sm:mb-6">
                    <div className="cv2-done-pill inline-flex items-center border rounded-full px-[19px] h-9">
                        <span className="cv2-done-cta font-spaceMono text-[11px] uppercase whitespace-nowrap">
                            {item.cta}
                        </span>
                    </div>
                </div>

                <p className="cv2-done-category font-spaceMono text-[9px] tracking-[0.5px] uppercase">
                    {item.category}
                </p>
            </div>
        </Component>
    );
};

const Done = ({ onBack, onNext, onLeave }) => (
    <OnboardingLayout stepKey="done" onBack={onBack}>
        <style>{SCOPED_STYLES}</style>
        <div className="flex-1 flex items-center justify-center px-7 sm:px-12 py-[72px]">
            <div className="max-w-[860px] w-full">
                <div className="mb-12">
                    <p className="cv2-done-eyebrow font-spaceMono text-[10px] tracking-[1.4px] uppercase mb-4">
                        Possibility Begins With Clarity
                    </p>
                    <h2 className="font-aeonik font-normal text-[42px] sm:text-[52px] leading-[48px] sm:leading-[58px] tracking-[-1.4px] text-cv2-foreground normal-case">
                        Clarity is now in motion.
                    </h2>
                    <p className="cv2-done-subcopy font-aeonik text-base leading-[26px] mt-3 normal-case max-w-[640px]">
                        Start with these three resources to introduce Claraview, align your team, and guide patients toward their next step.
                    </p>
                </div>

                <div className="cv2-done-card-row flex flex-col sm:flex-row sm:items-stretch border-y">
                    {NEXT_STEPS.map((item) => (
                        <NextStepCard key={item.title} item={item} onLeave={onLeave} />
                    ))}
                </div>

                <div className="mt-11 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-7">
                    <button
                        type="button"
                        onClick={onNext}
                        className="inline-flex items-center h-11 px-7 bg-claraview-secondary hover:brightness-110 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                    >
                        Open Dashboard
                    </button>
                    <button
                        type="button"
                        onClick={() => onLeave('/dashboard/resources')}
                        className="cv2-done-viewall font-spaceMono text-[10px] tracking-[0.8px] uppercase"
                    >
                        View all resources →
                    </button>
                </div>
            </div>
        </div>
    </OnboardingLayout>
);

export default Done;
